import { BankFilled } from "@ant-design/icons";
import { Alert, Form, Modal, Space } from "antd";
import Currency from "components/Currency";
import { useIdentity } from "context/auth";
import useAccountSummary from "hooks/useAccountSummary";
import { MouseEvent, useState } from "react";
import { centsToDollars, dollarsToCents } from "util/helpers";
import AmountFormItem from "../Payouts/RequestPayoutModal/PayoutMethodDetailForms/Inputs/AmountFormItem";
import { AlertState, TransferState } from "./TransferState";
import useFundsTransfer, { BalanceTransferType } from "./useFundsTransfer";

interface SilentAccountTransferModalProps {
    creditBalanceInCents: number;
    enabled: boolean;
    breakerId: string;
    breakerUserName: string;
    onFundsTransfer: () => Promise<void>;
}

const SilentAccountTransferModal = ({
    creditBalanceInCents,
    enabled,
    breakerId,
    breakerUserName,
    onFundsTransfer
}: SilentAccountTransferModalProps) => {
    const { accounts } = useIdentity();
    const { isLoading, refetch } = useAccountSummary(accounts[0]);
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm<{ amount: number }>();
    const [state, setState] = useState(() => TransferState.Start);
    const [preFlight, setPreFlight] = useState<{ amountInCents: number }>();
    const { transferFunds, isWorking } = useFundsTransfer(BalanceTransferType.SilentToConsignment);

    const noFunds = creditBalanceInCents === 0 && state === TransferState.Start;

    const close = (e: MouseEvent) => {
        e.stopPropagation();
        setOpen(false);
        setPreFlight(undefined);
        setState(TransferState.Start);
        form.resetFields();
    }

    const success = async () => {
        await refetch();
        await onFundsTransfer();
        setState(TransferState.Success);
    }

    const submit = async () => {
        setState(TransferState.Submitting);

        try {
            await transferFunds({
                amountInCents: preFlight!.amountInCents,
                breakerId
            });

            await success();
        } catch {
            setState(TransferState.Error);
        }
    }

    const nextStep = async (e: MouseEvent) => {
        switch (state) {
            case TransferState.Start: {
                const value = await form.validateFields();
                const amountInCents = dollarsToCents(value.amount.toString());

                setPreFlight({ amountInCents });
                setState(TransferState.Validated);
                break;
            }
            case TransferState.Validated: {
                await submit();
                break;
            }
            case TransferState.Success: {
                close(e);
            }
        }
    }

    const okText = state === TransferState.Success
        ? "OK"
        : state === TransferState.Validated
            ? "Transfer Funds"
            : "Next";

    return <Space direction="vertical" onClick={() => enabled && setOpen(true)}>
        <span style={enabled ? { textDecoration: 'underline' } : undefined}>
            Credit Balance {enabled && <BankFilled />}
        </span>
        <Currency cents={creditBalanceInCents} showZero />
        {enabled && <Modal
            title={`Transfer Balance with ${breakerUserName} to Available Balance`}
            open={open}
            onOk={nextStep}
            onCancel={close}
            destroyOnClose
            maskClosable={false}
            keyboard={false}
            closable={false}
            confirmLoading={isLoading || isWorking}
            okButtonProps={{ hidden: noFunds }}
            cancelButtonProps={{ disabled: isLoading || isWorking, hidden: state === TransferState.Success }}
            okText={okText}
        >
            <Form form={form} preserve={false} disabled={!!preFlight || noFunds}>
                <div style={{ fontSize: '26px' }}>Credit Balance: {centsToDollars(creditBalanceInCents)}</div>
                <p>Transfer funds from your credit balance with {breakerUserName} to your available balance.</p>

                <div style={{ marginTop: '20px' }}>
                    <AmountFormItem max={creditBalanceInCents} />

                    {noFunds && <Alert type="error" message="No funds available to transfer!" showIcon />}
                </div>
            </Form>

            <AlertState
                state={state}
                validatedMessage={`You will be transfering ${centsToDollars(preFlight?.amountInCents ?? 0)} to your available balance`}
            />
        </Modal>}
    </Space>;
}

export default SilentAccountTransferModal;